// Type
import { PointOptionsObject } from "highcharts";
import { BubbleSerie, FakeSerie, OptionsBubbleSeries } from "store/slice/charts";
import { BalanceSerie } from "store/slice/charts/chartsSlice.types";

type TSelectedSerie = {
    name: string;
    isCompany: boolean;
    index: number;
};
type UpdateSeriesWrapper = (
    isLegendClicked: React.MutableRefObject<boolean>,
    setChartOptions: React.Dispatch<React.SetStateAction<OptionsBubbleSeries>>,
    visibleSeries: React.MutableRefObject<number[]>
) => (chart: Highcharts.Chart) => void;

export const RADIUS_PROPORTION = 0.12;

export const getBalanceMaxSize = (chart: Highcharts.Chart): number => {
    return Math.min(chart.plotWidth || 0, chart.chartHeight || 0) * RADIUS_PROPORTION;
};

export const renderDotCircles = (
    chart: Highcharts.Chart,
    dotCircles: React.MutableRefObject<Highcharts.SVGElement[]>,
    options?: { isExporting?: boolean; onClickPoint?: Highcharts.PointClickCallbackFunction }
): void => {
    const maxSize = getBalanceMaxSize(chart);

    if (!maxSize) return;

    if (!options?.isExporting) {
        dotCircles.current.forEach((dotCircle) => dotCircle.destroy());
        dotCircles.current = [];
    }

    chart.series.forEach((serie) => {
        if (!serie.visible) return;

        serie.points.forEach((point) => {
            const pointOptions = point.options as Highcharts.PointOptionsObject | null;
            const z = pointOptions?.z;

            if (z == null || pointOptions?.marker?.enabled === false) return;
            if (typeof point.plotX !== "number" || typeof point.plotY !== "number") return;

            const radius = (z / 100) * maxSize;
            const x = chart.plotLeft + point.plotX;
            const y = chart.plotTop + point.plotY;
            const marker = {
                ...(serie.options.marker ?? {}),
                ...(pointOptions?.marker ?? {}),
            };

            const dotCircle = chart.renderer
                .circle(x, y, radius)
                .attr({
                    fill: marker.fillColor || point.color || serie.color,
                    stroke: marker.lineColor || point.color || serie.color,
                    "stroke-width": marker.lineWidth ?? 0,
                    "pointer-events": options?.onClickPoint ? "all" : "none",
                    zIndex: 3,
                })
                .css({
                    cursor: options?.onClickPoint ? "pointer" : "default",
                })
                .add();

            if (options?.onClickPoint) {
                dotCircle.element.addEventListener("click", (event) => {
                    event.stopPropagation();
                    options.onClickPoint?.call(point, event as unknown as Highcharts.PointClickEventObject);
                });
            }

            if (!options?.isExporting) {
                dotCircles.current.push(dotCircle);
            }
        });
    });
};

const generatePointInfo = (
    pointInfo: Highcharts.PointOptionsObject,
    dataLabelEnabled: boolean,
    markerEnabled: boolean
): Highcharts.PointOptionsObject => ({
    ...pointInfo,
    marker: { ...pointInfo.marker, enabled: markerEnabled },
    dataLabels: { ...pointInfo.dataLabels, enabled: dataLabelEnabled },
});

export const updateSeriesWrapper: UpdateSeriesWrapper = (isLegendClicked, setChartOptions, visibleSeries) => {
    const updateSeries = (chart: Highcharts.Chart) => {
        if (!isLegendClicked.current) return;

        const seriesRef: Highcharts.Series[] = chart.series;
        const selectedSeries: TSelectedSerie[] = seriesRef
            .filter(({ visible }) => visible)
            .map(({ name, index, options }) => ({ name, isCompany: options?.custom ? true : false, index }));
        const selectedSeriesId = selectedSeries.map(({ index }) => index);
        const companiesSelected = selectedSeries.filter(({ isCompany }) => isCompany).map(({ name }) => name);
        const rolesSelected = selectedSeries.filter(({ isCompany }) => !isCompany).map(({ name }) => name);

        isLegendClicked.current = false;

        setChartOptions(({ series, ...restOptions }) => {
            let newSeries: BalanceSerie[] = [];
            const generateData = (pointInfo: PointOptionsObject) => {
                const hasRole = rolesSelected.includes(pointInfo.name as string);

                // Show/hide the points of the company serie if the point is the role selected
                return hasRole ? generatePointInfo(pointInfo, true, true) : generatePointInfo(pointInfo, false, false);
            };

            // If no companies selected but there are roles selected, select all companies
            if (companiesSelected.length === 0 && rolesSelected.length) {
                newSeries =
                    series?.map((serie, index) => {
                        const isCompany = serie.kind === "real" && !!serie?.custom;
                        if (isCompany) visibleSeries.current.push(index);

                        return isCompany && serie.kind === "real"
                            ? {
                                  ...serie,
                                  data: (serie.data as Highcharts.PointOptionsObject[])?.map(generateData),
                                  visible: true,
                              }
                            : { ...serie, visible: seriesRef[index].visible ? true : false };
                    }) || [];
            } else {
                newSeries =
                    series?.map((serie, index) => {
                        const isSelected = selectedSeriesId.includes(index);
                        const isCompany = serie.kind === "real" && !!serie?.custom;

                        if (isSelected && isCompany && serie.kind === "real") {
                            // If the company has roles, show only these points
                            if (rolesSelected.length) {
                                const newData = (serie.data as Highcharts.PointOptionsObject[])?.map(generateData);
                                return { ...serie, data: newData, visible: isSelected } as BubbleSerie;
                            } else {
                                //if not roles selected, show all points of the company serie
                                const newData =
                                    (serie.data as Highcharts.PointOptionsObject[])?.map((pointInfo) =>
                                        generatePointInfo(pointInfo, true, true)
                                    ) || [];

                                return { ...serie, data: newData, visible: isSelected, type: "bubble" } as BubbleSerie;
                            }
                        } else return { ...serie, visible: isSelected, type: "scatter" } as FakeSerie;
                    }) || [];
            }

            return { ...restOptions, series: newSeries };
        });
    };

    return updateSeries;
};
